/** @jsxImportSource @emotion/react */

import React, { forwardRef, useMemo } from 'react';
import { ClipLoader } from 'react-spinners';
import { labelFromClickEvent, useAsyncButton } from 'ud-ui/hooks/use-async-button';
import UDText from '../text';

import { MainBlock } from './styles';
import { theme } from 'styles/theme/theme';

interface IProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  icon?: JSX.Element;
  color?: string;
  hoverColor?: string;
  disabledColor?: string;
  width?: number;
  height?: number;
  disabled?: boolean;
  centered?: boolean;
  /** Внешний спиннер — и выключает кнопку. `onClick`, вернувший промис, включает его сам и запирает кнопку до ответа. */
  loading?: boolean;
  loaderSize?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
}

const UDEmptyButton = forwardRef<HTMLButtonElement, IProps>((props, ref) => {
  const {
    icon,
    color = theme.colors.SurfaceRaisinBlack50,
    children,
    centered,
    loaderSize = 18,
    loading: externalLoading,
    disabled,
    onClick,
    ...rest
  } = props;

  // Тот же замок, что у `UDButton`: промис `onClick` держит кнопку выключенной со спиннером (IMGM-1561).
  const { isLoading, handleClick } = useAsyncButton(onClick, {
    label: (event?: unknown) => labelFromClickEvent(event) ?? 'EmptyButton',
  });
  const isBusy = Boolean(externalLoading) || isLoading;

  const body = useMemo(() => {
    if (isBusy) {
      // Подпись остаётся в доступном дереве (`sr-only` Tailwind): без неё у кнопки без `aria-label` на время
      // запроса пропадает имя, и скринридер читает безымянную «кнопку, занято».
      return (
        <>
          <ClipLoader size={loaderSize} />
          {children && <span className="sr-only">{children}</span>}
        </>
      );
    }

    return (
      <>
        {icon}
        {children && (
          <UDText style={{ color: 'inherit', fontSize: 'inherit' }} type={'subhead'}>
            {children}
          </UDText>
        )}
      </>
    );
  }, [children, icon, loaderSize, isBusy]);

  return (
    <MainBlock
      ref={ref}
      color={color}
      centered={centered ?? true}
      {...rest}
      // И по внешнему `loading`, как `UDButton` / `UDActionButton`: спиннер без замка пропускал Enter / пробел.
      disabled={disabled || isBusy}
      aria-busy={isBusy || undefined}
      onClick={onClick ? handleClick : undefined}
    >
      {body}
    </MainBlock>
  );
});

export default UDEmptyButton;
